import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarDays, Clock, Users, MapPin, Building2, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLang } from '@/i18n/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { classData as fallbackClassData, CAMPUSES, CLASS_TYPES } from '@/data/classes';
import { useGoogleSheets } from '@/hooks/useGoogleSheets';
import WaitlistModal from './WaitlistModal';

const ScheduleBuilder = () => {
  const { t, lang } = useLang();

  const MONTHS_ES = { January:'Enero', February:'Febrero', March:'Marzo', April:'Abril', May:'Mayo', June:'Junio', July:'Julio', August:'Agosto', September:'Septiembre', October:'Octubre', November:'Noviembre', December:'Diciembre' };
  const DAYS_ES = { Monday:'Lunes', Tuesday:'Martes', Wednesday:'Miércoles', Thursday:'Jueves', Friday:'Viernes', Saturday:'Sábado', Sunday:'Domingo', Mon:'Lun', Tue:'Mar', Wed:'Mié', Thu:'Jue', Fri:'Vie', Sat:'Sáb', Sun:'Dom', Thursdays:'Jueves' };

  const translateStr = (str) => {
    if (!str || lang !== 'es') return str;
    let s = str;
    Object.entries(MONTHS_ES).forEach(([en, es]) => { s = s.replace(new RegExp(en, 'g'), es); });
    Object.entries(DAYS_ES).forEach(([en, es]) => { s = s.replace(new RegExp(en, 'g'), es); });
    s = s.replace(/&/g, 'y');
    return s;
  };
  const [campus, setCampus] = useState('gastonia');
  const [type, setType] = useState('saturday');
  
  const { data: sheetsData, loading, error } = useGoogleSheets();
  const classData = sheetsData || fallbackClassData;
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);

  const navigate = useNavigate();

  const cohorts = useMemo(() => classData?.[campus]?.[type] ?? [], [campus, type, classData]);

  const openWaitlist = (cohort) => {
    setSelectedClass({
      ...cohort,
      campusKey: campus,
      typeKey: type,
      campusLabel: t.schedule.campuses[campus],
      typeLabel: t.schedule.types[type]
    });
    setWaitlistOpen(true);
  };

  const scrollToApply = (cohort) => {
    try {
      sessionStorage.setItem(
        'rdaa_prefill',
        JSON.stringify({ campus, type, cohortId: cohort.id })
      );
    } catch (_) {}

    window.dispatchEvent(new CustomEvent('rdaa-prefill', { detail: { campus, type } }));
    navigate('/application');
    window.scrollTo(0, 0);
  };

  return (
    <section id="schedule" className="relative py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold uppercase tracking-[0.18em] mb-4">
            <Sparkles className="w-3 h-3" /> {t.schedule.kicker}
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight">
            {t.schedule.title}
          </h2>
          <p className="mt-4 text-lg text-brand-gray leading-relaxed">{t.schedule.subtitle}</p>
        </div>

        <div className="max-w-5xl mx-auto bg-gradient-to-br from-slate-50 to-blue-50/40 border border-slate-200/80 rounded-2xl p-4 md:p-5 shadow-sm">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-gray mb-2.5">
                <Building2 className="w-3.5 h-3.5" /> {t.schedule.campusLabel}
              </div>
              <div className="flex gap-2" role="tablist">
                {CAMPUSES.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCampus(c)}
                    className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all border ${
                      campus === c
                        ? 'bg-brand-primary text-white border-blue-800 shadow-md shadow-blue-900/20'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-brand-primary hover:text-brand-primary'
                    }`}
                  >
                    {t.schedule.campuses[c]}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-gray mb-2.5">
                <CalendarDays className="w-3.5 h-3.5" /> {t.schedule.typeLabel}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {CLASS_TYPES.map((tp) => (
                  <button
                    key={tp}
                    onClick={() => setType(tp)}
                    className={`px-3 py-2.5 rounded-xl text-sm font-semibold transition-all border ${
                      type === tp
                        ? 'bg-slate-900 text-white border-slate-900 shadow-md'
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    {t.schedule.types[tp]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 max-w-6xl mx-auto">
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="py-12 text-center"
              >
                <div className="inline-block w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-slate-500 font-medium">
                  {lang === 'en' ? 'Loading latest schedules...' : 'Cargando horarios actualizados...'}
                </p>
              </motion.div>
            ) : (
              <motion.div
                key={`${campus}-${type}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-5"
              >
              {cohorts.length === 0 && (
                <div className="md:col-span-2 lg:col-span-3 text-center py-16 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                  <CalendarDays className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-brand-gray">{t.schedule.labels.none}</p>
                </div>
              )}

              {cohorts.map((cohort) => {
                if (cohort.isWaitlistOnly) {
                  return (
                    <motion.div
                      key={cohort.id}
                      whileHover={{ y: -4 }}
                      className="md:col-span-2 lg:col-span-3 bg-slate-900 text-white p-8 rounded-2xl relative overflow-hidden"
                    >
                      <div className="absolute top-0 right-0 p-8 opacity-[0.03] pointer-events-none">
                        <CalendarDays className="w-48 h-48 -mt-10 -mr-10 text-white" />
                      </div>
                      <div className="relative">
                        <h3 className="text-2xl font-bold mb-2 text-white">{t.schedule.types[type]}</h3>
                        <p className="text-slate-300 font-semibold mb-6">{translateStr(cohort.time)}</p>
                        <p className="text-slate-200 text-lg mb-6 max-w-3xl leading-relaxed">
                          {t.schedule.labels.waitlistMsg}
                        </p>
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                          <span className="font-semibold text-slate-200">{t.schedule.labels.emailLabel}</span>
                          <a href="mailto:info@royaldaacademy.com" className="text-blue-400 hover:text-white transition-colors font-semibold">info@royaldaacademy.com</a>
                        </div>
                      </div>
                    </motion.div>
                  );
                }

                const isFull = cohort.isFull;
                const almostFull = !isFull && cohort.seats > 0 && cohort.seats <= 5;
                return (
                  <motion.div
                    key={cohort.id}
                    whileHover={{ y: -4 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                    className={`relative bg-white rounded-2xl border ${
                      isFull ? 'border-rose-100' : almostFull ? 'border-amber-200' : 'border-slate-200/80'
                    } shadow-sm hover:shadow-xl transition-all p-6 overflow-hidden`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-gray">
                        <MapPin className="w-3 h-3" /> {t.schedule.campuses[campus]}
                      </span>
                      {isFull ? (
                        <span className="px-2.5 py-1 rounded-full bg-rose-100 text-rose-700 text-[10px] font-bold uppercase tracking-wider">
                          {t.schedule.labels.full}
                        </span>
                      ) : almostFull ? (
                        <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">
                          {t.schedule.labels.almostFull}
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold uppercase tracking-wider">
                          {t.schedule.labels.open}
                        </span>
                      )}
                    </div>

                    <h3 className="text-xl font-bold text-slate-900 leading-tight">
                      {t.schedule.types[type]}
                    </h3>
                    <p className="text-[12px] font-semibold text-slate-400 uppercase tracking-wider mt-0.5">
                      {t.schedule.labels.weeks}
                    </p>

                    <div className="mt-5 space-y-2.5 text-sm">
                      <div className="flex items-start gap-2.5">
                        <CalendarDays className="w-4 h-4 text-brand-primary mt-0.5 shrink-0" />
                        <div>
                          <div className="text-brand-gray text-[11px] uppercase tracking-wider font-semibold">{t.schedule.labels.start} → {t.schedule.labels.end}</div>
                          <div className="text-slate-900 font-semibold">{translateStr(cohort.startDate)} — {translateStr(cohort.endDate)}</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <Clock className="w-4 h-4 text-brand-primary mt-0.5 shrink-0" />
                        <div>
                          <div className="text-brand-gray text-[11px] uppercase tracking-wider font-semibold">{t.schedule.labels.time}</div>
                          <div className="text-slate-900 font-semibold">{translateStr(cohort.time)}</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-2.5">
                        <Users className="w-4 h-4 text-brand-primary mt-0.5 shrink-0" />
                        <div>
                          <div className="text-brand-gray text-[11px] uppercase tracking-wider font-semibold">{t.schedule.labels.seats}</div>
                          <div className={`font-semibold ${isFull ? 'text-rose-600' : 'text-slate-900'}`}>
                            {isFull ? t.schedule.labels.full : `${cohort.seats} ${t.schedule.labels.seatsLeft}`}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6">
                      {isFull ? (
                        <Button
                          onClick={() => openWaitlist(cohort)}
                          className="w-full bg-brand-primary hover:opacity-80 text-white rounded-xl h-11 font-semibold"
                        >
                          {t.schedule.labels.waitlist}
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      ) : (
                        <Button
                          onClick={() => scrollToApply(cohort)}
                          className="w-full bg-brand-primary hover:from-blue-800 hover:to-blue-950 text-white rounded-xl h-11 font-semibold shadow-md shadow-blue-900/15"
                        >
                          {t.schedule.labels.enroll}
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <WaitlistModal
        open={waitlistOpen}
        onClose={() => setWaitlistOpen(false)}
        cohort={selectedClass}
      />
    </section>
  );
};

export default ScheduleBuilder;
